import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';

const hasMouse = () => window.matchMedia('(any-pointer: fine)').matches || window.matchMedia('(pointer: fine)').matches;

export default function CursorAura({
  text,
  colors = { color1: '#FFE45C', color2: '#3FB9A3', color3: '#1F8A7E', color4: '#FFB98A' },
  className = '',
  as: Tag = 'h2',
}) {
  const rootRef = useRef(null);
  const maskRef = useRef(null);
  const points = useRef([{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }]);
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'off';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'off';
    return hasMouse() ? 'pointer' : 'auto';
  });

  useEffect(() => {
    const root = rootRef.current;
    const mask = maskRef.current;
    const pts = points.current;
    if (mode === 'off' || !root || !mask) return undefined;

    const apply = () => {
      mask.style.setProperty('--x-outer', `${pts[0].x}px`);
      mask.style.setProperty('--y-outer', `${pts[0].y}px`);
      mask.style.setProperty('--x-middle', `${pts[1].x}px`);
      mask.style.setProperty('--y-middle', `${pts[1].y}px`);
      mask.style.setProperty('--x-center', `${pts[2].x}px`);
      mask.style.setProperty('--y-center', `${pts[2].y}px`);
    };

    const b = mask.getBoundingClientRect();
    pts.forEach((p) => { p.x = b.width * 0.3; p.y = b.height / 2; });
    apply();

    if (mode === 'auto') {
      const tl = gsap.timeline({ repeat: -1, yoyo: true, defaults: { ease: 'sine.inOut' } });
      tl.to(pts, {
        x: () => mask.getBoundingClientRect().width * 0.92,
        y: () => mask.getBoundingClientRect().height * 0.35,
        duration: 3.2,
        stagger: 0.18,
        onUpdate: apply,
      }).to(pts, {
        x: () => mask.getBoundingClientRect().width * 0.08,
        y: () => mask.getBoundingClientRect().height * 0.65,
        duration: 3.2,
        stagger: 0.18,
        onUpdate: apply,
      });
      const toPointer = (e) => {
        if (e.pointerType === 'mouse' || e.pointerType === 'pen') setMode('pointer');
      };
      root.addEventListener('pointermove', toPointer);
      return () => {
        root.removeEventListener('pointermove', toPointer);
        tl.kill();
        gsap.killTweensOf(pts);
      };
    }

    const move = (e) => {
      const r = mask.getBoundingClientRect();
      gsap.to(pts, {
        x: e.clientX - r.left,
        y: e.clientY - r.top,
        duration: 0.45,
        ease: 'power2.out',
        stagger: -0.08,
        overwrite: 'auto',
        onUpdate: apply,
      });
    };

    root.addEventListener('pointermove', move);
    return () => {
      root.removeEventListener('pointermove', move);
      gsap.killTweensOf(pts);
    };
  }, [mode]);

  const live = mode !== 'off';

  return (
    <div ref={rootRef} className={`ob-aura ${live ? 'is-live' : ''} ${mode === 'auto' ? 'is-auto' : ''} ${className}`}>
      <div className="ob-aura__inner">
        <Tag className="ob-aura__text">{text}</Tag>
        {live && (
          <span
            ref={maskRef}
            aria-hidden="true"
            className="ob-aura__text ob-aura__mask"
            style={{
              backgroundImage: `
                radial-gradient(circle min(130px, 14vw) at var(--x-center) var(--y-center), ${colors.color3} 0 58%, transparent 100%),
                radial-gradient(circle min(220px, 23vw) at var(--x-middle) var(--y-middle), ${colors.color2} 0 48%, transparent 100%),
                radial-gradient(circle min(330px, 34vw) at var(--x-outer) var(--y-outer), ${colors.color1} 0 42%, transparent 100%),
                radial-gradient(circle min(440px, 45vw) at var(--x-outer) var(--y-outer), ${colors.color4} 0 40%, transparent 100%)
              `,
            }}
          >
            {text}
          </span>
        )}
      </div>
    </div>
  );
}
